/**
 * Generic descriptor-driven per-unit edit body (#997 — Story 3.3).
 *
 * The one edit body the descriptor engine renders for any section that declares
 * attachments and/or Additional Information. It composes, from the descriptor
 * alone and the shared helpers:
 *
 *   1. the per-unit fields (the engine's `renderInstanceFields`) inside the main
 *      card, with the FIRST attachment base section's attachments;
 *   2. one attachment-only card per additional attachment base section (except
 *      the Additional Information section, which the card in step 3 owns);
 *   3. an Additional Information card bound to `descriptor.additionalInfoField`
 *      — to the PER-UNIT instance value when the field is not shared (the Racks
 *      case) and to the shared/request-level field when it is (the Small UPS
 *      case).
 *
 * This mirrors the peer `SmallUpsInstanceEditFormBody` but is section-agnostic:
 * everything is derived from the descriptor, so no hand-authored per-section body
 * is needed. It reuses the generic engine via the `renderEditFormContent` escape
 * hatch — the engine itself is NOT modified.
 */

import React from 'react';
import { Platform } from 'react-native';

import { descriptorFieldsInGroups } from './sectionDescriptor.types';
import { FormStackColumnsOnWebProvider } from '../../components/forms';
import { RepeatableInstanceAdditionalInfo } from '../../components/repeatableSections/repeatableInstanceAdditionalInfo';
import { RepeatableInstanceEditFormContent } from '../../components/repeatableSections/RepeatableInstanceEditFormContent';
import { resolveRepeatableInstanceAttachmentSectionId } from '../../utils/repeatableInstanceAttachmentSections';

import type { SectionDescriptor } from './sectionDescriptor.types';
import type { RepeatableInstanceEditFormRenderProps } from '../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

interface DescriptorInstanceEditFormBodyProps
  extends RepeatableInstanceEditFormRenderProps {
  descriptor: SectionDescriptor;
}

/** Header fill-count metric fields for one editor card's field group (#997 6.3). */
function editorCardMetricFields(
  descriptor: SectionDescriptor,
  fieldGroupKeys: readonly string[],
): readonly SubsectionFieldDescriptor[] {
  return descriptorFieldsInGroups(descriptor, fieldGroupKeys)
    .slice(0, 4)
    .map(field => ({ key: field.dataField, shortLabel: field.shortLabel }));
}

export function DescriptorInstanceEditFormBody({
  descriptor,
  editFormTitle,
  editFormSubtitle,
  editFormMetricFields,
  sectionId,
  configurationId,
  instanceFields,
  setInstanceField,
  sharedFields,
  setSharedField,
  disabled,
  requestStage,
  requestId,
  showFullDetails,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
  renderInstanceFields,
  renderInstanceFieldsInGroups,
  testID,
}: DescriptorInstanceEditFormBodyProps): React.ReactElement {
  const baseSectionIds = descriptor.attachmentBaseSectionIds ?? [sectionId];
  const additionalInfoField = descriptor.additionalInfoField;
  const hasAdditionalInfo = additionalInfoField != null;

  // Convention (matches the live sections): the Additional Information card owns
  // the LAST attachment base section; the earlier ones are field/attachment cards.
  const additionalInfoSectionId =
    hasAdditionalInfo && baseSectionIds.length > 0
      ? baseSectionIds[baseSectionIds.length - 1]
      : undefined;
  // Scope every per-unit attachment card by the unit's configurationId so an
  // upload on one unit does not leak onto another (#997 Story 6.1). Mirrors the
  // bespoke RackEditFormContent (`const configurationId = formData.id`). The
  // shared additional-info case is handled below (it stays on its per-step id).
  const mainSectionId = resolveRepeatableInstanceAttachmentSectionId(
    baseSectionIds[0] ?? sectionId,
    configurationId,
  );
  const middleSectionIds = baseSectionIds
    .slice(1, hasAdditionalInfo ? -1 : undefined)
    .map(id =>
      resolveRepeatableInstanceAttachmentSectionId(id, configurationId),
    );

  const additionalInfoIsShared = additionalInfoField
    ? descriptor.sharedFieldKeys.includes(additionalInfoField)
    : false;

  const additionalInfoValue = additionalInfoField
    ? additionalInfoIsShared
      ? String(sharedFields[additionalInfoField] ?? '')
      : String(instanceFields[additionalInfoField] ?? '')
    : '';

  const additionalInfoLabel =
    (additionalInfoField &&
      descriptor.fields.find(f => f.dataField === additionalInfoField)
        ?.label) ||
    'Additional Information';

  const onAdditionalInfoChange = (value: string): void => {
    if (!additionalInfoField) {
      return;
    }
    if (additionalInfoIsShared) {
      setSharedField(additionalInfoField, value);
    } else {
      setInstanceField(additionalInfoField, value);
    }
  };

  // Field-cards: when the descriptor declares `editorCards` (the live Racks path,
  // #997 Story 6.3), each card renders ONLY its field group so per-unit fields
  // land in the correct card. Otherwise fall back to the pre-6.3 shape — one main
  // card with ALL fields plus attachment-only middle shells.
  const fieldCards: Array<{
    key: string;
    title: string;
    titleSubtitle?: string;
    scopedSectionId: string;
    metricFields: readonly SubsectionFieldDescriptor[];
    children: React.ReactNode;
    testID: string;
  }> =
    descriptor.editorCards &&
    descriptor.editorCards.length > 0 &&
    renderInstanceFieldsInGroups
      ? descriptor.editorCards.map((card, index) => ({
          key: card.baseSectionId,
          title: card.title,
          titleSubtitle: index === 0 ? editFormSubtitle : undefined,
          scopedSectionId: resolveRepeatableInstanceAttachmentSectionId(
            card.baseSectionId,
            configurationId,
          ),
          metricFields: editorCardMetricFields(descriptor, card.fieldGroupKeys),
          children: renderInstanceFieldsInGroups(card.fieldGroupKeys),
          testID:
            index === 0 ? `${testID}-main` : `${testID}-${card.baseSectionId}`,
        }))
      : [
          {
            key: 'main',
            title: editFormTitle,
            titleSubtitle: editFormSubtitle,
            scopedSectionId: mainSectionId,
            metricFields: editFormMetricFields,
            children: renderInstanceFields,
            testID: `${testID}-main`,
          },
          ...middleSectionIds.map(cardSectionId => ({
            key: cardSectionId,
            title: editFormTitle,
            titleSubtitle: undefined,
            scopedSectionId: cardSectionId,
            metricFields: [] as readonly SubsectionFieldDescriptor[],
            children: null as React.ReactNode,
            testID: `${testID}-${cardSectionId}`,
          })),
        ];

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      {fieldCards.map(card => (
        <RepeatableInstanceEditFormContent
          key={card.key}
          title={card.title}
          titleSubtitle={card.titleSubtitle}
          sectionId={card.scopedSectionId}
          requestId={requestId}
          requestStage={requestStage}
          headerValues={instanceFields}
          metricFields={card.metricFields}
          disabled={disabled}
          attachments={attachmentsBySection[card.scopedSectionId] ?? []}
          onAttachFiles={
            onAttachFiles
              ? () => onAttachFiles(card.scopedSectionId)
              : undefined
          }
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles[card.scopedSectionId] ?? []}
          onFilesSelected={
            onFilesSelected
              ? files => {
                  markDirty();
                  onFilesSelected(card.scopedSectionId, files);
                }
              : undefined
          }
          isOffline={offlineMode}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          testID={card.testID}
        >
          {card.children}
        </RepeatableInstanceEditFormContent>
      ))}

      {additionalInfoField && additionalInfoSectionId ? (
        <RepeatableInstanceAdditionalInfo
          titleSubtitle="Notes, constraints, or details not captured above."
          sectionId={resolveRepeatableInstanceAttachmentSectionId(
            additionalInfoSectionId,
            // Per-unit additional-info (Racks) scopes by the unit; a SHARED
            // request-level additional-info field (Small UPS / PD / LU) stays on
            // its unscoped per-step id — unchanged from the migrated bodies.
            additionalInfoIsShared ? undefined : configurationId,
          )}
          fieldId={additionalInfoField}
          inputLabel={additionalInfoLabel}
          placeholder=""
          value={additionalInfoValue}
          onChangeText={onAdditionalInfoChange}
          requestId={requestId}
          requestStage={requestStage}
          disabled={disabled}
          headerValues={additionalInfoIsShared ? sharedFields : instanceFields}
          attachmentsBySection={attachmentsBySection}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles}
          onFilesSelected={onFilesSelected}
          offlineMode={offlineMode}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          markDirty={markDirty}
          showFullDetails={showFullDetails}
        />
      ) : null}
    </FormStackColumnsOnWebProvider>
  );
}
